import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { updateUserProfile } from "../services/firestoreService";
import { UserRole } from "../types";

const MAX_AVATAR_MB = 5;

const StaffAccount: React.FC = () => {
  const { userDoc, user, firmId: authFirmId, isFirmOwner, staffRole } = useAuth();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [signature, setSignature] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const [localPreview, setLocalPreview] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Prefer AuthContext firmId, fall back to doc fields and session
  const firmId = useMemo(() => {
    return (
      authFirmId ||
      (userDoc as any)?.firmId ||
      (userDoc as any)?.locationId ||
      sessionStorage.getItem("FILERSHUB_SSO_LOC") ||
      null
    );
  }, [authFirmId, userDoc]);

  const isPrivileged = useMemo(() => {
    return (
      isFirmOwner ||
      staffRole === "Manager" ||
      userDoc?.role === UserRole.FirmOwner ||
      userDoc?.role === UserRole.Manager
    );
  }, [isFirmOwner, staffRole, userDoc]);

  // Resolve avatar url across possible field names + local preview
  const avatarUrl = useMemo(() => {
    return (
      localPreview ||
      (userDoc as any)?.avatarUrl ||
      (userDoc as any)?.photoUrl ||
      (userDoc as any)?.photoURL ||
      (userDoc as any)?.profilePhotoUrl ||
      ""
    );
  }, [localPreview, userDoc]);

  // Keep form state in sync when userDoc loads/updates
  useEffect(() => {
    if (!userDoc) return;

    setDisplayName((userDoc as any)?.displayName || "");
    setSignature((userDoc as any)?.messageSignature || "");
    setAvatarFile(null);
    setLocalPreview("");
    setMessage("");
  }, [userDoc]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setMessage("");

    if (!f.type.match(/^image\//)) {
      setMessage("Please select a valid image file (PNG or JPEG).");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const maxBytes = MAX_AVATAR_MB * 1024 * 1024;
    if (f.size > maxBytes) {
      setMessage(`File size must be less than ${MAX_AVATAR_MB}MB.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setAvatarFile(f);
    setLocalPreview(URL.createObjectURL(f));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setMessage("You are not signed in.");
      return;
    }
    if (!firmId) {
      setMessage("Missing firm context. Refresh and try again.");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      await updateUserProfile(
        firmId,
        user.uid,
        {
          // Only let privileged users change name
          ...(isPrivileged ? { displayName } : {}),
          messageSignature: signature,
        },
        avatarFile || undefined
      );

      setMessage("Profile updated successfully!");
      setAvatarFile(null);

      // Allow re-uploading the same file
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error(error);
      setMessage("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-2 md:p-8">
      <h1 className="text-2xl font-bold text-slate-800 mb-2">My Account</h1>
      <p className="text-slate-500 mb-8">Manage your profile visibility and communication preferences.</p>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Profile Picture */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h2 className="font-bold text-slate-800 mb-4 uppercase tracking-widest text-xs">Profile Picture</h2>

          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full border-4 border-white shadow-md bg-slate-100 overflow-hidden flex items-center justify-center">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile Preview"
                    className="w-full h-full object-cover"
                    onError={() => setLocalPreview("")}
                  />
                ) : (
                  <span className="text-slate-400 font-black text-xs">No Photo</span>
                )}
              </div>

              <label
                htmlFor="avatar-upload"
                className="absolute bottom-0 right-0 bg-brand-600 text-white p-1.5 rounded-full cursor-pointer hover:bg-brand-700 shadow-sm border-2 border-white"
                title="Upload avatar"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </label>

              <input
                ref={fileInputRef}
                type="file"
                id="avatar-upload"
                accept="image/png, image/jpeg"
                className="hidden"
                onChange={handleFileChange}
                disabled={saving}
              />
            </div>

            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700 mb-1">Your Avatar</p>
              <p className="text-xs text-slate-500">
                This image will be visible to clients next to your name in their "My Tax Pro" section and in messaging threads.
              </p>
              <p className="text-[11px] text-slate-400 mt-2">Max size: {MAX_AVATAR_MB}MB. PNG or JPG recommended.</p>
            </div>
          </div>
        </div>

        {/* Info & Signature */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-slate-800 uppercase tracking-widest text-xs">Personal Details</h2>
            {!isPrivileged && (
              <span className="text-[10px] bg-slate-100 text-slate-400 font-black uppercase px-2 py-1 rounded tracking-widest">
                Read-Only Name
              </span>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-widest">Display Name</label>
            <input
              type="text"
              value={displayName}
              disabled={!isPrivileged}
              onChange={(e) => setDisplayName(e.target.value)}
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none ${
                !isPrivileged ? "bg-slate-50 text-slate-400 cursor-not-allowed font-medium" : "bg-white"
              }`}
            />
            {!isPrivileged && (
              <p className="text-[10px] text-slate-400 mt-1 italic">Contact your firm manager to request a name change.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-widest">Email Signature</label>
            <textarea
              rows={4}
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder={`Best regards,\n${displayName || "Your Name"}\n${(userDoc as any)?.firmName || "Filers Hub"}`}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none bg-white"
            />
            <p className="text-xs text-slate-400 mt-1">This signature will be appended to system emails sent on your behalf.</p>
          </div>
        </div>

        {message && (
          <div
            className={`p-3 rounded-lg text-sm ${
              message.toLowerCase().includes("success") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
            }`}
          >
            {message}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-brand-600 text-white px-6 py-2.5 rounded-lg font-medium uppercase tracking-widest hover:bg-brand-700 disabled:opacity-70 shadow-md transition-colors"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StaffAccount;
